import React, { useState, useEffect } from 'react';
import { DollarSign, X, Trash2, CheckCircle } from 'lucide-react';

const EditPaymentModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  versement,
  modesPaiement,
  editLoading = false,
  studentName
}) => {
  const [formData, setFormData] = useState({
    montant: '',
    commentaire: '',
    mode_paiement: '',
    lignes_versement: []
  });
  
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Initialiser le formulaire avec les données du versement
  useEffect(() => {
    if (versement) {
      setFormData({
        montant: versement.montant || '',
        commentaire: versement.commentaire || '',
        mode_paiement: versement.mode_paiement?.id || '',
        lignes_versement: versement.lignes_versement ? [...versement.lignes_versement] : []
      });
    }
  }, [versement]);

  if (!isOpen || !versement) return null;

  // Gérer les changements dans les lignes de versement
  const handleLigneChange = (index, field, value) => {
    const updatedLignes = [...formData.lignes_versement];
    updatedLignes[index] = {
      ...updatedLignes[index],
      [field]: value
    };
    
    setFormData({
      ...formData,
      lignes_versement: updatedLignes
    });
    
    // Si le champ modifié est le montant, mettre à jour le montant total
    if (field === 'montant') {
      updateTotalAmount(updatedLignes);
    }
  };

  // Mettre à jour le montant total quand les lignes changent
  const updateTotalAmount = (lignes = formData.lignes_versement) => {
    const total = lignes.reduce((sum, ligne) => sum + Number(ligne.montant || 0), 0);
    setFormData(prev => ({ ...prev, montant: total, lignes_versement: lignes }));
  };

  // Gestion du changement de champs simples
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Soumettre le formulaire
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData, versement.id);
  };

  // Supprimer le versement
  const handleDelete = () => {
    if (confirmDelete) {
      onSubmit({ ...formData, deleted: true }, versement.id);
    } else {
      setConfirmDelete(true);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full overflow-hidden">
        <div className="bg-[#202654] text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Modifier le versement</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label htmlFor="montant" className="block text-sm font-medium text-gray-700 mb-1">
                  Montant total (FCFA) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    id="montant"
                    name="montant"
                    value={formData.montant}
                    readOnly
                    className="block w-full px-4 py-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm bg-gray-50"
                    required
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Le montant total est calculé automatiquement</p>
              </div>
              <div>
                <label htmlFor="mode_paiement" className="block text-sm font-medium text-gray-700 mb-1">
                  Mode de paiement <span className="text-red-500">*</span>
                </label>
                <select
                  id="mode_paiement"
                  name="mode_paiement"
                  value={formData.mode_paiement}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                  required
                >
                  <option value="">Sélectionner un mode de paiement</option>
                  {modesPaiement.map((mode) => (
                    <option key={mode.id} value={mode.id}>
                      {mode.libelle}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="commentaire" className="block text-sm font-medium text-gray-700 mb-1">
                Commentaire
              </label>
              <textarea
                id="commentaire"
                name="commentaire"
                value={formData.commentaire}
                onChange={handleInputChange}
                rows="2"
                placeholder="Ajoutez des détails sur ce versement..."
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
              ></textarea>
            </div>

            <div className="border rounded-lg p-4 bg-gray-50">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Lignes de versement <span className="text-red-500">*</span>
              </label>
              
              {formData.lignes_versement.map((ligne, index) => (
                <div key={ligne.id || index} className="mb-3 p-3 border rounded bg-white">
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-6">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Libellé</label>
                      <input
                        type="text"
                        value={ligne.libelle || ''}
                        onChange={(e) => handleLigneChange(index, 'libelle', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        required
                      />
                    </div>
                    <div className="col-span-3">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Montant</label>
                      <input
                        type="number"
                        value={ligne.montant || ''}
                        onChange={(e) => handleLigneChange(index, 'montant', Number(e.target.value))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        required
                      />
                    </div>
                    <div className="col-span-3">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                      <select
                        value={ligne.type_ligne || 'SCOLARITE'}
                        onChange={(e) => handleLigneChange(index, 'type_ligne', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        disabled={ligne.option} // Désactiver si c'est lié à une option
                      >
                        <option value="SCOLARITE">Frais de scolarité</option>
                        <option value="INSCRIPTION">Frais d'inscription</option>
                        <option value="OPTION">Option complémentaire</option>
                        <option value="AUTRE">Autre frais</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-800">
                    Vous modifiez un versement pour l'étudiant {studentName}.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            <button
              type="button"
              onClick={handleDelete}
              className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                confirmDelete ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
            >
              {confirmDelete ? 'Confirmer la suppression' : 'Supprimer le versement'}
            </button>
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={editLoading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] disabled:opacity-50"
              >
                {editLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Traitement...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2 inline-block" />
                    Enregistrer les modifications
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPaymentModal; 